import { useState, useRef } from "react";
import {
  useAdminRoles,
  useAdminCreateRole,
  useAdminUpdateRole,
  useAdminDeleteRole,
} from "../hooks/useApi";
import "../admin-theme.css";

const PERMISSION_LABELS: Record<string, string> = {
  "users.view": "مشاهده کاربران",
  "users.manage": "ویرایش و مسدود کردن کاربران",
  "tickets.view": "مشاهده تیکت‌ها",
  "tickets.manage": "پاسخ، بستن و باز کردن تیکت‌ها",
  "models.view": "مشاهده مدل‌ها",
  "models.manage": "مدیریت مدل‌ها و قیمت‌گذاری",
  "logs.view": "مشاهده لاگ‌ها",
  "logs.manage": "بازیابی و تسویه لاگ‌ها",
  "stats.view": "مشاهده آمار و داشبورد",
  "settings.view": "مشاهده تنظیمات",
  "settings.manage": "مدیریت تنظیمات",
  "system.view": "مشاهده وضعیت سیستم",
  "roles.manage": "مدیریت نقش‌ها",
  "expenses.view": "مشاهده هزینه‌ها",
  "expenses.manage": "ثبت و مدیریت هزینه‌ها",
  "subscriptions.view": "مشاهده پلن‌های اشتراک",
  "subscriptions.manage": "مدیریت پلن‌های اشتراک",
  "messages.view": "مشاهده اطلاع‌رسانی‌ها",
  "messages.manage": "مدیریت اطلاع‌رسانی‌ها",
  "partners.view": "مشاهده شرکا (Partner)",
  "partners.manage": "مدیریت شرکا، موجودی و کلیدهای Partner",
  "factors.view": "مشاهده فاکتورها (پرداخت‌ها)",
};

const ALL_PERMISSIONS = Object.keys(PERMISSION_LABELS);

interface RoleForm {
  role_id?: number;
  name: string;
  display_name: string;
  description: string;
  permissions: string[];
}

const EMPTY_FORM: RoleForm = { name: "", display_name: "", description: "", permissions: [] };

export default function AdminRoles() {
  const { data: roles, isLoading } = useAdminRoles();
  const createRole = useAdminCreateRole();
  const updateRole = useAdminUpdateRole();
  const deleteRole = useAdminDeleteRole();

  const [showForm, setShowForm] = useState(false);
  const overlayMouseDown = useRef(false);
  const [form, setForm] = useState<RoleForm>(EMPTY_FORM);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (role: Record<string, unknown>) => {
    setForm({
      role_id: role.role_id as number,
      name: role.name as string,
      display_name: role.display_name as string,
      description: (role.description as string) || "",
      permissions: (role.permissions as string[]) || [],
    });
    setShowForm(true);
  };

  const togglePermission = (perm: string) => {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(perm)
        ? f.permissions.filter((p) => p !== perm)
        : [...f.permissions, perm],
    }));
  };

  const handleSave = () => {
    if (!form.name.trim() || !form.display_name.trim()) return;
    const payload = {
      name: form.name.trim(),
      display_name: form.display_name.trim(),
      description: form.description.trim() || null,
      permissions: form.permissions,
    };
    if (form.role_id != null) {
      updateRole.mutate({ id: String(form.role_id), data: payload });
    } else {
      createRole.mutate(payload);
    }
    setShowForm(false);
  };

  const handleDelete = (role: Record<string, unknown>) => {
    if (!window.confirm(`نقش «${role.display_name}» حذف شود؟`)) return;
    deleteRole.mutate(String(role.role_id));
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "var(--admin-text-primary)" }}>نقش‌ها</h1>
          <p className="text-sm mt-1" style={{ color: "var(--admin-text-secondary)" }}>
            تعریف نقش‌های دسترسی مانند مالی، پشتیبانی و ...
          </p>
        </div>
        <button className="admin-btn admin-btn-primary" onClick={openCreate}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          نقش جدید
        </button>
      </div>

      {/* Roles Grid */}
      <div className="admin-card overflow-hidden">
        <div className="admin-card-header">
          <h2 className="admin-card-title">لیست نقش‌ها</h2>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24" fill="none" style={{ color: "var(--admin-primary)" }}>
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        ) : !roles?.length ? (
          <div className="flex flex-col items-center justify-center py-16" style={{ color: "var(--admin-text-tertiary)" }}>
            <p className="text-sm">هیچ نقشی تعریف نشده است</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 p-6">
            {(roles as Record<string, unknown>[]).map((role) => (
              <div
                key={role.role_id as string}
                className="rounded-2xl p-5 border"
                style={{ background: "var(--admin-bg-surface)", borderColor: "var(--admin-border)" }}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-semibold" style={{ color: "var(--admin-text-primary)" }}>
                        {role.display_name as string}
                      </h3>
                      {Boolean(role.is_system) && (
                        <span className="admin-badge admin-badge-info" style={{ fontSize: 10 }}>سیستمی</span>
                      )}
                    </div>
                    <p className="text-xs mt-0.5" style={{ color: "var(--admin-text-tertiary)" }} dir="ltr">
                      {role.name as string}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      className="admin-btn admin-btn-secondary admin-btn-sm"
                      onClick={() => openEdit(role)}
                    >
                      ویرایش
                    </button>
                    <button
                      className="admin-btn admin-btn-sm"
                      style={{
                        background: "var(--admin-error-bg)",
                        color: "var(--admin-error)",
                        border: "none",
                        opacity: Boolean(role.is_system) ? 0.4 : 1,
                        cursor: Boolean(role.is_system) ? "not-allowed" : "pointer",
                      }}
                      disabled={Boolean(role.is_system)}
                      title={Boolean(role.is_system) ? "نقش‌های سیستمی قابل حذف نیستند" : "حذف نقش"}
                      onClick={() => handleDelete(role)}
                    >
                      حذف
                    </button>
                  </div>
                </div>

                {(role.description as string) && (
                  <p className="text-xs mb-3" style={{ color: "var(--admin-text-secondary)" }}>
                    {role.description as string}
                  </p>
                )}

                <div className="flex flex-wrap gap-1.5">
                  {((role.permissions as string[]) || []).map((perm) => (
                    <span
                      key={perm}
                      className="admin-badge"
                      style={{
                        background: "var(--admin-primary-light)",
                        color: "var(--admin-primary-dark)",
                        fontSize: 11,
                      }}
                    >
                      {PERMISSION_LABELS[perm] || perm}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {showForm && (
        <div
          className="admin-overlay"
          onMouseDown={(e) => { overlayMouseDown.current = e.target === e.currentTarget; }}
          onClick={(e) => { if (e.target === e.currentTarget && overlayMouseDown.current) setShowForm(false); overlayMouseDown.current = false; }}
        >
          <div
            className="admin-glass admin-modal"
            style={{ maxWidth: 560 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-base font-semibold" style={{ color: "var(--admin-text-primary)" }}>
                {form.role_id != null ? "ویرایش نقش" : "نقش جدید"}
              </h2>
              <button
                onClick={() => setShowForm(false)}
                className="p-1 rounded-lg transition-colors"
                style={{ color: "var(--admin-text-tertiary)" }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                  نام (اسلاگ)
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="admin-input"
                  placeholder="support"
                  dir="ltr"
                  disabled={Boolean(form.role_id != null && (roles as Record<string, unknown>[]).find((r) => r.role_id === form.role_id)?.is_system)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                  نام نمایشی
                </label>
                <input
                  type="text"
                  value={form.display_name}
                  onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
                  className="admin-input"
                  placeholder="پشتیبانی"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                  توضیحات
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  className="admin-input"
                  style={{ height: "auto", minHeight: 80, paddingTop: 12, paddingBottom: 12, resize: "vertical" }}
                  placeholder="شرح نقش..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: "var(--admin-text-secondary)" }}>
                  دسترسی‌ها
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-xl p-3" style={{ background: "var(--admin-bg-hover)", border: "1px solid var(--admin-border)" }}>
                  {ALL_PERMISSIONS.map((perm) => (
                    <label
                      key={perm}
                      className="flex items-center gap-2.5 px-1 py-1 rounded-lg cursor-pointer text-sm"
                      style={{ color: "var(--admin-text-primary)" }}
                    >
                      <input
                        type="checkbox"
                        checked={form.permissions.includes(perm)}
                        onChange={() => togglePermission(perm)}
                        style={{ accentColor: "var(--admin-primary)" }}
                      />
                      {PERMISSION_LABELS[perm]}
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowForm(false)}
                  className="admin-btn admin-btn-secondary flex-1"
                >
                  انصراف
                </button>
                <button
                  onClick={handleSave}
                  disabled={createRole.isPending || updateRole.isPending || !form.name.trim() || !form.display_name.trim()}
                  className="admin-btn admin-btn-primary flex-1"
                >
                  ذخیره
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
